import { LabRowData } from "../../tables/lab/lab-rows";
import { ObservationSeries } from "../../table-transforms/series-utils";
import { FeaturedCategory } from "../categories";
import { TableRow } from "../../utils/tables-metadata";

/**
 * LOINC codes for eGFR (estimated Glomerular Filtration Rate) observations
 */
const EGFR_LOINC_CODES = [
  "98979-8",
  "88294-4",
  "48643-1",
  "88293-6",
  "48642-3",
  "33914-3",
  "62238-1",
  "69405-9",
  "77147-7",
  "45066-8",
  "98980-6",
  "50210-4",
  "103542-7",
  "50384-7",
  "50044-7",
  "50383-9",
  "69406-7",
  "70969-1",
  "97952-6",
  "72726-3",
  "96591-3",
  "96592-1",
  "76633-7",
  "94677-2",
  "87430-5",
];

/**
 * LOINC codes for A1C (Hemoglobin A1c) observations
 */
const A1C_LOINC_CODES = [
  "71875-9", // Hemoglobin A1c/Hemoglobin.total [Pure mass fraction] in Blood
  "59261-8", // Hemoglobin A1c/Hemoglobin.total in Blood by IFCC protocol
  "4549-2", // Hemoglobin A1c/Hemoglobin.total in Blood by Electrophoresis
  "4548-4", // Hemoglobin A1c/Hemoglobin.total in Blood
  "55454-3", // Deprecated Hemoglobin A1c in Blood
  "17856-6", // Hemoglobin A1c/Hemoglobin.total in Blood by HPLC
  "17855-8", // Hemoglobin A1c/Hemoglobin.total in Blood by calculation
  "41995-2", // Hemoglobin A1c [Mass/volume] in Blood
];

/**
 * LOINC codes for BNP (B-type Natriuretic Peptide) and NT-proBNP observations
 */
const BNP_LOINC_CODES = [
  "33762-6", // Natriuretic peptide.B prohormone N-Terminal [Mass/volume] in Serum or Plasma
  "42637-9", // Natriuretic peptide B [Mass/volume] in Blood
  "83107-3", // Natriuretic peptide.B prohormone N-Terminal [Mass/volume] in Serum or Plasma by Immunoassay
  "33763-4", // Natriuretic peptide.B prohormone N-Terminal [Moles/volume] in Serum or Plasma
  "30934-4", // Natriuretic peptide B [Mass/volume] in Serum or Plasma
];

/**
 * LOINC codes for Urine Microalbumin (UMA or UACR) observations
 */
const MICROALBUMIN_CREATININE_RATIO_LOINC_CODES = [
  // Albumin/Creatinine
  "111467-7",
  "32294-1",
  "9318-7",
  "76401-9",
  "14585-4",
  "13705-9",
  "44707-8",
  "20621-9",
  "30000-4",
  "14959-1",
  "59159-4",
  "44292-1",
  "14958-3",
  "77253-3",
  "89998-9",
  "30001-2",
  "95233-3",
  "77254-1",
];

/**
 * Filter eGFR (estimated Glomerular Filtration Rate) labs for Featured section
 */
export function getFeaturedEGFRLabs(labs: LabRowData[]): LabRowData[] {
  return labs.filter(lab => {
    const series = lab.originalData as ObservationSeries;
    const code = series.code;
    return EGFR_LOINC_CODES.some(loincCode => code.includes(loincCode));
  });
}

/**
 * Filter A1C (Hemoglobin A1c) labs for Featured section
 */
export function getFeaturedA1CLabs(labs: LabRowData[]): LabRowData[] {
  return labs.filter(lab => {
    const series = lab.originalData as ObservationSeries;
    const code = series.code;
    return A1C_LOINC_CODES.some(loincCode => code.includes(loincCode));
  });
}

/**
 * Filter BNP (B-type Natriuretic Peptide) and NT-proBNP labs for Featured section
 */
export function getFeaturedBNPLabs(labs: LabRowData[]): LabRowData[] {
  return labs.filter(lab => {
    const series = lab.originalData as ObservationSeries;
    const code = series.code;
    return BNP_LOINC_CODES.some(loincCode => code.includes(loincCode));
  });
}

/**
 * Filter Urine Microalbumin (UMA or UACR) labs for Featured section
 */
export function getFeaturedUrineMicroalbuminLabs(labs: LabRowData[]): LabRowData[] {
  return labs.filter(lab => {
    const series = lab.originalData as ObservationSeries;
    const code = series.code;
    return MICROALBUMIN_CREATININE_RATIO_LOINC_CODES.some(loincCode => code.includes(loincCode));
  });
}

export function getFeaturedLabs(): FeaturedCategory {
  return {
    category: "Labs",
    subCategories: [
      {
        subCategory: "eGFR (Glomerular Filtration Rate)",
        tableType: "LaboratoryObservations",
        getRows: (data: TableRow[]) => getFeaturedEGFRLabs(data as LabRowData[]),
      },
      {
        subCategory: "A1C (Hemoglobin A1c)",
        tableType: "LaboratoryObservations",
        getRows: (data: TableRow[]) => getFeaturedA1CLabs(data as LabRowData[]),
      },
      {
        subCategory: "BNP (B-type Natriuretic Peptide)",
        tableType: "LaboratoryObservations",
        getRows: (data: TableRow[]) => getFeaturedBNPLabs(data as LabRowData[]),
      },
      {
        subCategory: "Microalbumin/Creatinine Ratio",
        tableType: "LaboratoryObservations",
        getRows: (data: TableRow[]) => getFeaturedUrineMicroalbuminLabs(data as LabRowData[]),
      },
    ],
  };
}
